import React, {Component} from 'react';
import {debounce} from 'lodash';
import {
    Input,
    Flex,
    CheckBox,
    Text,
    SelectV2,
    SelectSizes,
    PriorityIcon,
    Icon,
    noop
} from '@priority-software/priority-style-react';
import {priority, wcfclient} from '@priority-software/priority-proto-api/lib';
import {getLocalizedText, texts} from '../../../../constants/TextConst';
import {isRTL} from '../../../../Localization';
import styles from './styles.module.scss';
import {getIDbyColumnName} from '../../../../utils/proto-api-utils/utils';
import {getAdditionalProps} from '../../../../services/proto/core/loginProps';

import {getForm} from '../../service/api/general';

type IFieldSelectRq = priority.netitems.form.IFieldSelectRq;

type SelectOption = {
    label: string;
    value: string;
    description?: string;
};

type Props = any & {
    formName: string;
    name: string;
    displayedValueFormatter: (optionData) => string;
    value: string;
    horizontalMenuPosition?: 'left' | 'right' | 'auto';
    Preferred: priority.netitems.shared.FieldSelectType;
};

type State = {
    requestParams: IFieldSelectRq;
    open: boolean;
    loading: boolean;
    HaveMore?: boolean;
    selectedOption?: SelectOption;
    options: Array<SelectOption>;
    Preferred: priority.netitems.shared.FieldSelectType;
};

const debounceTimeout = 650;
const fieldSelectType = priority.netitems.shared.FieldSelectType;

const getNoOptionsText = () => getLocalizedText(texts.noSearchResults);

export class AsyncSelect extends Component<Props, State> {
    static defaultProps = {
        value: '',
        horizontalMenuPosition: 'left',
        Preferred: priority.netitems.shared.FieldSelectType.Choose
    };

    constructor(props) {
        super(props);
        this.state = {
            requestParams: {
                Properties: getAdditionalProps(),
                FromStart: false,
                IgnoreCase: false,
                Page: 1,
                Pattern: ''
            },
            open: false,
            loading: false,
            options: [],
            HaveMore: false,
            selectedOption: {value: props.value, label: props.value},
            Preferred: props.Preferred
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        if (this.props.value === '' && this.state.selectedOption.value !== this.props.value) {
            this.setState({selectedOption: {value: '', label: ''}});
        }
    }

    toggleCaseSensitive = async () => {
        await this.requestData({IgnoreCase: !this.state.requestParams.IgnoreCase});
    };

    searchFromStart = async () => {
        await this.requestData({FromStart: !this.state.requestParams.FromStart});
    };

    onScrollEnd = async () => {
        if (this.state.HaveMore) {
            await this.requestData({Page: Number(this.state.requestParams.Page) + 1}, true);
        }
    };

    isChooseType = () => this.state.Preferred === fieldSelectType.Choose;

    onMenuClose = () => {
        this.setState({
            open: false,
            requestParams: {
                ...this.state.requestParams,
                Page: 1,
                Pattern: ''
            }
        });
    };

    requestData = async (params = {}, loadMore?: boolean) => {
        try {
            if (this.state.requestParams) {
                const {form, cols} = await getForm(this.props.formName);

                const ColumnID = getIDbyColumnName(cols.Columns, this.props.name);

                if (ColumnID) {
                    const requestParams = {
                        ...this.state.requestParams,
                        ...params,
                        ColumnID,
                        Session: form.Session
                    };

                    this.setState({loading: !loadMore, requestParams}); // show the loader only for initial request

                    const reqParams = new priority.netitems.form.FieldSelectRq(requestParams);

                    const data = await wcfclient.fieldSelect(reqParams);

                    const mappedOptions = data.Values.map(params => {
                        return {
                            value: params.Value,
                            label: params.Value,
                            description: params.Description || params.Description2
                        };
                    });

                    const options = loadMore
                        ? [...this.state.options, ...mappedOptions]
                        : mappedOptions;

                    this.setState({
                        options,
                        loading: false,
                        HaveMore: data.HaveMore
                    });
                } else {
                    console.error(`There is missing columnID for '${this.props.name}' Column`);
                }
            }
        } catch (e) {
            this.setState({loading: false});
            console.error(e);
        }
    };

    debouncedRequest = debounce(async (params: any = {}) => {
        await this.requestData(params);
    }, debounceTimeout);

    onInputChange = (Pattern: string, meta: {action: string}) => {
        const trimmedPattern = !!Pattern.trim() ? Pattern : ''; // Validate empty pattern value #19819

        if (meta.action === 'input-change') {
            if (!this.isChooseType()) {
                // Requesting new options for non `Choose` type select.
                this.debouncedRequest({Pattern: trimmedPattern, Page: 1});
            }
        }
        return trimmedPattern;
    };

    onMenuOpen = async e => {
        if (!this.state.open) {
            this.setState({open: true});
            await this.requestData();
        }
    };

    onOptionChange = (option, data) => {
        this.props.onChange(option, data);
        this.setState({selectedOption: option});
    };

    getDisplayedValue = (): string => {
        const {value, displayedValueFormatter} = this.props;
        const {selectedOption} = this.state;

        if (displayedValueFormatter && selectedOption) {
            return displayedValueFormatter(selectedOption);
        }
        return value || '';
    };

    renderHeader = () => {
        const {IgnoreCase, FromStart} = this.state.requestParams;
        if (this.isChooseType()) {
            return null;
        }
        return (
            <Flex justifyContent="space-between">
                <CheckBox
                    componentClasses={styles.headerCheckbox}
                    checked={IgnoreCase}
                    onChange={this.toggleCaseSensitive}
                >
                    <Text
                        componentClasses={styles.label}
                        textColor="secondary-400"
                        skin="caption-2"
                    >
                        {getLocalizedText(texts.ignorecase)}
                    </Text>
                </CheckBox>

                <CheckBox
                    componentClasses={styles.headerCheckbox}
                    checked={FromStart}
                    onChange={this.searchFromStart}
                >
                    <Text
                        componentClasses={styles.label}
                        textColor="secondary-400"
                        skin="caption-2"
                    >
                        {getLocalizedText(texts.searchFromBegining)}
                    </Text>
                </CheckBox>
            </Flex>
        );
    };

    filterOptions = (candidate: {label: string; value: string; data: any}, input: string) => {
        if (input) {
            const query = input.toLowerCase();
            return (
                candidate.data.label.toLowerCase().includes(query) ||
                candidate.data?.description?.toLowerCase()?.includes(query)
            );
        }
        return true;
    };

    onFocus = e => e.currentTarget?.select();

    render() {
        const {name, components = {}, ...selectProps} = this.props;

        const {open, loading, selectedOption, options} = this.state;

        /*
         * Passing null value for non `Choose` select type. It prevents filtering on the client side.
         *  Passing custom `filterOptions` method enables the default client side filter behavior.
         */
        const preventedFilterOption = !this.isChooseType() ? null : this.filterOptions;

        return (
            <div className={styles.component}>
                <div className={styles.inputHolder} onClick={this.onMenuOpen}>
                    <Input
                        onFocus={this.onMenuOpen}
                        componentClasses={styles.input}
                        size="large"
                        onChange={noop}
                        value={this.getDisplayedValue()}
                        placeholder={selectProps.placeholder}
                    >
                        <Icon componentClasses={styles.arrowIcon} icon={PriorityIcon.arrowDown} />
                    </Input>
                </div>

                {open && (
                    <div className={styles.dropdown}>
                        <SelectV2
                            componentClasses={styles.select2}
                            size={SelectSizes.large}
                            menuPosition="absolute"
                            onFocus={this.onFocus}
                            name={name}
                            {...selectProps}
                            autoFocus
                            filterOption={preventedFilterOption}
                            onScrollEnd={this.onScrollEnd}
                            onChange={this.onOptionChange}
                            menuScrollMaxHeight={220}
                            menuIsOpen
                            controlShouldRenderValue={false}
                            menuPortalTarget={null}
                            options={!loading ? options : []}
                            value={selectedOption}
                            onInputChange={this.onInputChange}
                            onMenuClose={this.onMenuClose}
                            isRtl={isRTL()}
                            noOptionsMessage={getNoOptionsText}
                            openMenuOnFocus
                            components={{
                                ...components,
                                LoadingIndicator: null
                            }}
                            isSearchable
                        />
                    </div>
                )}
            </div>
        );
    }
}
